import { useTranslation } from "react-i18next";
import { useState, useEffect, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Pencil } from "lucide-react";
import { ENABLE_COMMITMENT_PLEDGES } from "@/config/features";

const Loading = () => {
  const { t } = useTranslation();

  const [currentStage, setCurrentStage] = useState(0);
  const [showOutputs, setShowOutputs] = useState(false);

  // Calculate total stages based on feature flags (AI scan hidden)
  const totalStages = ENABLE_COMMITMENT_PLEDGES ? 4 : 3; // 4 with accountability, 3 without (AI scan hidden)

  // Cycle through stages with variable timing
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    
    const nextStage = () => {
      setCurrentStage((prev) => {
        const next = (prev + 1) % totalStages;
        setShowOutputs(false);
        
        // Schedule next transition based on the stage we're entering
        let timing = 2500; // default
        // if (next === 2) timing = 9000; // scan stage - extended for full animation (hidden)
        if (ENABLE_COMMITMENT_PLEDGES && next === 2) timing = 3500; // accountability stage (only if enabled)
        if (next === (ENABLE_COMMITMENT_PLEDGES ? 3 : 2)) timing = 4000; // custom metrics stage
        
        timeout = setTimeout(nextStage, timing);
        
        return next;
      });
    };
    
    // Initial delay
    const getCurrentTiming = () => {
      // if (currentStage === 2) return 9000; // scan stage (hidden)
      if (ENABLE_COMMITMENT_PLEDGES && currentStage === 2) return 3500;
      if (currentStage === (ENABLE_COMMITMENT_PLEDGES ? 3 : 2)) return 4000;
      return 2500;
    };
    
    timeout = setTimeout(nextStage, getCurrentTiming());
    
    return () => clearTimeout(timeout);
  }, [currentStage, totalStages]);

  // Show outputs for scanning stage immediately (currently hidden)
  useEffect(() => {
    // AI scan stage is hidden, so this effect is disabled
    // if (currentStage === 2) {
    //   setShowOutputs(true);
    // } else {
      setShowOutputs(false);
    // }
  }, [currentStage]);

  const renderStage = () => {
    if (!ENABLE_COMMITMENT_PLEDGES) {
      // When commitment pledges are disabled, skip the accountability stage
      switch (currentStage) {
        case 0:
          return <ContributionChart />;
        case 1:
          return <AppNameWithMetrics />;
        // case 2:
        //   return <ScanningImage showOutputs={showOutputs} />;
        case 2:
          return <CustomMetricsScreen />;
        default:
          return null;
      }
    }
    
    // Original stage order when commitment pledges are enabled
    switch (currentStage) {
      case 0:
        return <ContributionChart />;
      case 1:
        return <AppNameWithMetrics />;
      // case 2:
      //   return <ScanningImage showOutputs={showOutputs} />;
      case 2:
        return <AccountabilityScreen />;
      case 3:
        return <CustomMetricsScreen />;
      // Disabled stages:
      // case 2: return <ScanningImage showOutputs={showOutputs} />;
      // case 3: return <LeaderboardScreen />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 overflow-hidden relative">
      {/* Ambient background glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{
            opacity: [0.03, 0.06, 0.03],
            scale: [1, 1.1, 1],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full"
          style={{ background: 'radial-gradient(circle, hsl(var(--contrib-3)) 0%, transparent 70%)' }}
        />
      </div>

      <div className="w-full max-w-md flex flex-col items-center relative z-10">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStage}
            initial={{ opacity: 0, scale: 0.95, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 1.02, y: -8 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="w-full"
          >
            {renderStage()}
          </motion.div>
        </AnimatePresence>
      </div>
      
      {/* Elegant grid-wave loading indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.8 }}
        className="fixed bottom-8 left-0 right-0 flex justify-center items-center"
      >
        <LoadingWave />
      </motion.div>
    </div>
  );
};

// Stage 0: Contribution Chart
const ContributionChart = () => {
  const weeks = Array.from({ length: 12 }, (_, i) => i);
  const days = Array.from({ length: 7 }, (_, i) => i);

  const getIntensity = (week: number, day: number) => {
    const value = (week * 7 + day + Math.random() * 30) % 110;
    if (value >= 100) return 4;
    if (value >= 75) return 3;
    if (value >= 50) return 2;
    if (value >= 25) return 1;
    return 0;
  };

  const getColor = (intensity: number) => {
    const colors = [
      "bg-[hsl(var(--contrib-0))]",
      "bg-[hsl(var(--contrib-1))]",
      "bg-[hsl(var(--contrib-2))]",
      "bg-[hsl(var(--contrib-3))]",
      "bg-[hsl(var(--contrib-4))]",
    ];
    return colors[intensity];
  };

  return (
    <div className="space-y-6 flex flex-col items-center justify-center py-4 px-2">
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="flex gap-1 justify-center p-4 bg-card rounded-xl shadow-lg border border-border max-w-full overflow-hidden"
      >
        {weeks.map((week) => (
          <div key={week} className="flex flex-col gap-1">
            {days.map((day) => {
              const intensity = getIntensity(week, day);
              return (
                <motion.div
                  key={day}
                  initial={{ scale: 0, rotate: -90, opacity: 0 }}
                  animate={{ scale: 1, rotate: 0, opacity: 1 }}
                  transition={{
                    delay: (week * 7 + day) * 0.003,
                    duration: 0.3,
                    type: "spring",
                    stiffness: 400,
                    damping: 25,
                  }}
                  className={`w-3 h-3 rounded-sm ${getColor(intensity)}`}
                />
              );
            })}
          </div>
        ))}
      </motion.div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="flex items-center justify-center gap-2 text-xs"
      >
        <span className="text-muted-foreground font-medium">{t('metrics_tour.instructions.chart_less')}</span>
        {[0, 1, 2, 3, 4].map((i) => (
          <motion.div
            key={i}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
              delay: 0.6 + i * 0.05,
              type: "spring",
              stiffness: 350,
            }}
            className={`w-3 h-3 rounded-sm ${getColor(i)}`}
          />
        ))}
        <span className="text-muted-foreground font-medium">{t('metrics_tour.instructions.chart_more')}</span>
      </motion.div>
    </div>
  );
};

// Stage 1: App Name with Metrics Above
const AppNameWithMetrics = () => {
  const metrics = [
    { emoji: "🔥", progress: 85 },
    { emoji: "🍗", progress: 60 },
    { emoji: "🏃", progress: 45 },
    { emoji: "💪", progress: 92 },
    { emoji: "😴", progress: 75 },
    { emoji: "💧", progress: 40 },
  ];

  const getProgressColor = (progress: number) => {
    if (progress >= 100) return "rgb(34, 197, 94)"; // green
    if (progress >= 75) return "rgb(132, 204, 22)"; // lime
    if (progress >= 50) return "rgb(234, 179, 8)"; // yellow
    if (progress >= 25) return "rgb(249, 115, 22)"; // orange
    return "rgb(239, 68, 68)"; // red
  };

  return (
    <div className="flex flex-col items-center justify-center space-y-6 py-4 px-2">
      {/* Scrolling Metrics with fade in/out */}
      <div className="relative overflow-hidden h-20 w-full">
        <motion.div
          animate={{
            x: [0, -150],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "linear",
          }}
          className="flex gap-4 absolute"
        >
          {[...metrics, ...metrics, ...metrics, ...metrics].map((metric, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ 
                opacity: 1, 
                scale: 1,
              }}
              transition={{
                delay: idx * 0.02,
                type: "spring",
                stiffness: 200,
              }}
              className="flex-shrink-0 relative"
            >
              <div 
                className="w-16 h-16 rounded-full bg-white flex items-center justify-center text-2xl relative overflow-hidden border border-accent/10"
              >
                {/* Progress ring */}
                <svg className="absolute inset-0 w-full h-full -rotate-90">
                  <circle
                    cx="32"
                    cy="32"
                    r="28"
                    fill="none"
                    stroke={getProgressColor(metric.progress)}
                    strokeWidth="4"
                    style={{
                      strokeDasharray: `${2 * Math.PI * 28}`,
                      strokeDashoffset: `${2 * Math.PI * 28 * (1 - metric.progress / 100)}`,
                    }}
                    opacity="0.9"
                  />
                </svg>
                <span className="relative z-10">{metric.emoji}</span>
              </div>
            </motion.div>
          ))}
        </motion.div>
        
        {/* Gradient masks for fade effect */}
        <div className="absolute inset-y-0 left-0 w-20 bg-gradient-to-r from-background to-transparent pointer-events-none z-10" />
        <div className="absolute inset-y-0 right-0 w-20 bg-gradient-to-l from-background to-transparent pointer-events-none z-10" />
      </div>

      {/* App Name */}
      <div className="flex flex-col items-center gap-2">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="text-4xl md:text-5xl font-bold text-foreground"
        >
          gitfit.ai
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.6 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-sm text-muted-foreground"
        >{t('ui.text.daily_tracking_your_goals_only')}</motion.p>
      </div>
    </div>
  );
};


// Stage 2: Scanning Image with Camera Snap
const ScanningImage = ({ showOutputs }: { showOutputs: boolean }) => {
  const [showCamera, setShowCamera] = useState(false);
  const [showFlash, setShowFlash] = useState(false);
  const [userText, setUserText] = useState("");
  const [showButton, setShowButton] = useState(false);
  const fullText = "cooked in olive oil";
  const hasAnimatedRef = useRef(false);

  // Typing animation for user text
  useEffect(() => {
    let index = 0;
    setUserText("");
    const timer = setInterval(() => {
      if (index <= fullText.length) {
        setUserText(fullText.slice(0, index));
        index++;
      } else {
        clearInterval(timer);
      }
    }, 100);
    return () => clearInterval(timer);
  }, []);

  // Camera emoji appears first, then flash - only once
  useEffect(() => {
    if (hasAnimatedRef.current) return;
    hasAnimatedRef.current = true;
    
    const cameraTimer = setTimeout(() => {
      setShowCamera(true);
      // Flash happens 300ms after camera appears
      const flashTimer = setTimeout(() => {
        setShowFlash(true);
        setTimeout(() => {
          setShowFlash(false);
          setShowCamera(false);
        }, 400);
      }, 300);
      return () => clearTimeout(flashTimer);
    }, 1200); // Show camera at 1.2s
    return () => clearTimeout(cameraTimer);
  }, []);

  // Show button after outputs appear
  useEffect(() => {
    if (showOutputs) {
      const timer = setTimeout(() => setShowButton(true), 4000); // Show button at 4s
      return () => clearTimeout(timer);
    } else {
      setShowButton(false);
    }
  }, [showOutputs]);

  return (
    <div className="flex flex-col items-center justify-center py-3 px-4 min-h-[280px] w-full max-w-md mx-auto">
      {/* User typing text - AI input field */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 0 }}
        animate={{ 
          opacity: 1, 
          scale: 1,
          y: showOutputs ? -15 : 0
        }}
        transition={{ 
          opacity: { duration: 0.4, ease: [0.34, 1.56, 0.64, 1] },
          scale: { duration: 0.4, ease: [0.34, 1.56, 0.64, 1] },
          y: { duration: 0.6, delay: 0.3, ease: [0.34, 1.56, 0.64, 1] }
        }}
        className="w-full mb-2"
      >
        <div className="relative w-full bg-card border border-border rounded-lg shadow-md px-3 py-2 flex items-center gap-2">
          {/* AI Sparkles Icon */}
          <motion.div
            animate={{ 
              opacity: [0.6, 1, 0.6]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="flex-shrink-0"
          >
            <Sparkles className="w-3.5 h-3.5 text-purple-500" />
          </motion.div>
          
          {/* Text input area */}
          <div className="flex-1 text-xs text-foreground font-medium">
            {userText}
            {userText.length < fullText.length && (
              <span className="inline-block w-0.5 h-3 bg-accent ml-0.5 animate-pulse" />
            )}
          </div>
        </div>
      </motion.div>

      {/* Main Card - Food icon and nutrition stats */}
      <motion.div
        initial={{ opacity: 0, scale: 0.85, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ 
          delay: 0.5,
          duration: 0.5,
          ease: [0.34, 1.56, 0.64, 1]
        }}
        className="w-full bg-card rounded-xl border border-border shadow-lg p-3 mb-2"
      >
            {/* Camera emoji and flash effects */}
            <div className="relative mb-3 h-24 flex items-center justify-center rounded-lg bg-gradient-to-br from-accent/20 via-accent/10 to-accent/5 border border-accent/30 overflow-hidden">
              <AnimatePresence>
                {showCamera && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ 
                      duration: 0.2,
                      ease: [0.34, 1.56, 0.64, 1]
                    }}
                    className="absolute inset-0 flex items-center justify-center z-30 pointer-events-none"
                  >
                    <div className="text-6xl drop-shadow-lg">📸</div>
                  </motion.div>
                )}
              </AnimatePresence>
              
              <AnimatePresence>
                {showFlash && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0, 0.95, 0.85, 0] }}
                    exit={{ opacity: 0 }}
                    transition={{ 
                      duration: 0.4,
                      times: [0, 0.3, 0.6, 1],
                      ease: "easeOut"
                    }}
                    className="absolute inset-0 bg-white rounded-2xl z-20 shadow-[0_0_30px_8px_rgba(255,255,255,0.8)]"
                  />
                )}
              </AnimatePresence>

              {/* Scanning Bar */}
              <motion.div
                className="absolute left-0 right-0 h-1 bg-gradient-to-r from-transparent via-accent to-transparent z-10"
                style={{ 
                  boxShadow: "0 0 15px hsl(var(--accent) / 0.8)" 
                }}
                animate={{
                  top: ["0%", "100%", "0%"],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />

              {/* Food Emoji - centered */}
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 2.0, duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
                className="text-5xl text-center relative z-10"
              >
                🍗
              </motion.div>
            </div>

            {/* Nutrition stats - inline compact layout */}
            <div className="flex gap-2 items-center justify-center">
              {[
                { label: "Protein", value: "46g", icon: "💪", delta: 12, total: 150 },
                { label: "Calories", value: "412", icon: "🔥", delta: 412, total: 2000 },
              ].map((output, idx) => (
                <motion.div
                  key={output.label}
                  initial={{ opacity: 0, scale: 0.85, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{
                    delay: 2.6 + idx * 0.25,
                    duration: 0.4,
                    ease: [0.34, 1.56, 0.64, 1],
                  }}
                  className="flex-1 bg-gradient-to-br from-accent/10 to-accent/5 border border-accent/20 rounded-lg p-2 shadow-sm"
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm">{output.icon}</span>
                      <div className="flex flex-col">
                        <span className="text-[9px] text-muted-foreground font-medium uppercase tracking-wide">{output.label}</span>
                        <span className="text-base font-bold text-accent leading-none">{output.value}</span>
                      </div>
                    </div>
                    <button className="p-0.5 hover:bg-accent/10 rounded transition-colors">
                      <Pencil className="w-3 h-3 text-muted-foreground" />
                    </button>
                  </div>
                  {/* Progress bar */}
                  <div className="w-full h-1 bg-muted rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.min((output.delta / output.total) * 100, 100)}%` }}
                      transition={{ delay: 2.8 + idx * 0.25, duration: 0.8, ease: "easeOut" }}
                      className={`h-full ${
                        output.delta > output.total * 0.7 ? 'bg-red-500' : 'bg-green-500'
                      }`}
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

      {/* Healthier choice tip - below card */}
      <motion.div
        initial={{ opacity: 0, y: 15, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ 
          delay: 3.3,
          duration: 0.5,
          ease: [0.34, 1.56, 0.64, 1]
        }}
        className="w-full bg-yellow-50 border border-yellow-200 rounded-lg p-2 flex items-center gap-2 mb-3 shadow-sm"
      >
        <span className="text-base flex-shrink-0">💡</span>
        <p className="text-[10px] text-yellow-900 leading-relaxed">
          <span className="font-semibold">{t('ui.text.healthier_choice')}</span>{t('ui.text.bake_or_air_fry_instead_to_reduce_oil_calories')}</p>
      </motion.div>

      {/* Add to Daily Totals button - larger and centered */}
      <AnimatePresence>
        {showButton && (
          <motion.button
            initial={{ opacity: 0, y: 10, scale: 0.85 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.85 }}
            transition={{ 
              duration: 0.5,
              ease: [0.34, 1.56, 0.64, 1]
            }}
            className="w-full bg-green-500 hover:bg-green-600 text-white px-6 py-2.5 rounded-lg font-semibold text-sm shadow-lg transition-colors"
          >{t('ui.text.add_to_daily_totals')}</motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};


// Stage 3: Leaderboard Screen
const LeaderboardScreen = () => {
  const leaderboardData = [
    { rank: 1, name: "alex_runner", score: 2847, emoji: "🔥" },
    { rank: 2, name: "fitness_sam", score: 2621, emoji: "💪" },
    { rank: 3, name: "you", score: 2458, emoji: "⭐", highlight: true },
    { rank: 4, name: "gym_maya", score: 2301, emoji: "🏃" },
    { rank: 5, name: "health_pro", score: 2156, emoji: "💯" },
  ];

  return (
    <div className="flex flex-col items-center justify-center py-4 px-4 space-y-3 min-h-[280px]">
      <motion.h2
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-xl font-bold text-foreground"
      >{t('ui.text.share_compete_connect')}</motion.h2>
      
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
        className="w-full max-w-sm bg-card rounded-xl border border-border p-3 space-y-1.5"
      >
        <div className="flex items-center justify-between mb-2 pb-1.5 border-b border-border/50">
          <span className="text-xs text-muted-foreground font-medium">{t('ui.text.leaderboard')}</span>
          <span className="text-xs text-accent font-semibold">🏆</span>
        </div>
        
        {leaderboardData.map((user, idx) => (
          <motion.div
            key={user.rank}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 + idx * 0.1, type: "spring", stiffness: 200 }}
            className={`flex items-center justify-between p-1.5 rounded-lg ${
              user.highlight 
                ? "bg-accent/10 border border-accent/30" 
                : "bg-accent/5"
            }`}
          >
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-muted-foreground w-4">
                {user.rank}
              </span>
              <span className="text-base">{user.emoji}</span>
              <span className={`text-xs ${user.highlight ? "font-bold text-accent" : "text-foreground"}`}>
                {user.name}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-xs font-bold text-accent">{user.score}</span>
            </div>
          </motion.div>
        ))}
      </motion.div>
      
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.5 }}
        transition={{ delay: 1 }}
        className="text-xs text-muted-foreground text-center"
      >{t('ui.text.compete_with_friends_on_your_terms')}</motion.p>
    </div>
  );
};


// Stage 4: Accountability Screen
const AccountabilityScreen = () => {
  const [visibleWords, setVisibleWords] = useState(0);
  const words = [
    { text: "If", highlight: false },
    { text: "I", highlight: false },
    { text: "don't", highlight: true },
    { text: "run", highlight: false },
    { text: "5", highlight: true },
    { text: "miles,", highlight: true },
    { text: "donate", highlight: false },
    { text: "$50", highlight: true },
    { text: "to", highlight: false },
    { text: "charity", highlight: false },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setVisibleWords((prev) => {
        if (prev >= words.length) {
          clearInterval(interval);
          return prev;
        }
        return prev + 1;
      });
    }, 200); // Each word appears every 200ms

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex flex-col items-center justify-center py-4 px-4 space-y-6 min-h-[280px]">
      <motion.h2
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-xl font-bold text-foreground"
      >{t('ui.text.set_your_consequences')}</motion.h2>
      
      {/* Consequence card with word-by-word animation */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
        className="w-full max-w-sm bg-card rounded-xl border border-border p-6 shadow-lg"
      >
        <div className="flex items-center justify-center gap-2 text-lg font-medium text-foreground flex-wrap min-h-[80px]">
          {words.slice(0, visibleWords).map((word, idx) => (
            <motion.span
              key={idx}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className={word.highlight ? "text-red-500 font-bold" : ""}
            >
              {word.text}
            </motion.span>
          ))}
        </div>
      </motion.div>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.5 }}
        transition={{ delay: 2.5, duration: 0.4 }}
        className="text-xs text-muted-foreground text-center"
      >{t('ui.text.real_stakes_real_results')}</motion.p>
    </div>
  );
};


// Stage 5: Custom Metrics Screen
const CustomMetricsScreen = () => {
  const [currentMetricIndex, setCurrentMetricIndex] = useState(0);
  
  const metrics = [
    { name: "Practiced Piano", value: "45 min", icon: "🎹", color: "from-purple-50 to-purple-100/50", border: "border-purple-300/60", text: "text-purple-900" },
    { name: "Appearance Rating", value: "8.5", icon: "✨", subtitle: "with AI", color: "from-pink-50 to-pink-100/50", border: "border-pink-300/60", text: "text-pink-900" },
    { name: "GitHub Commits", value: "12", icon: "💻", color: "from-slate-50 to-slate-100/50", border: "border-slate-300/60", text: "text-slate-900" },
    { name: "Calories", value: "2,240", icon: "🔥", color: "from-orange-50 to-orange-100/50", border: "border-orange-300/60", text: "text-orange-900" },
    { name: "Fiber", value: "28g", icon: "🥗", color: "from-green-50 to-green-100/50", border: "border-green-300/60", text: "text-green-900" },
    { name: "Protein", value: "156g", icon: "💪", color: "from-blue-50 to-blue-100/50", border: "border-blue-300/60", text: "text-blue-900" },
    { name: "Meditated", value: "20 min", icon: "🧘", color: "from-indigo-50 to-indigo-100/50", border: "border-indigo-300/60", text: "text-indigo-900" },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMetricIndex((prev) => (prev + 1) % metrics.length);
    }, 550); // Change every 550ms for better viewing
    
    return () => clearInterval(interval);
  }, [metrics.length]);

  const currentMetric = metrics[currentMetricIndex];

  return (
    <div className="flex flex-col items-center justify-center py-4 px-4 space-y-6 min-h-[320px]">
      <motion.h2
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-2xl font-bold text-foreground text-center"
      >{t('ui.text.track_anything_with_custom_metrics')}</motion.h2>
      
      <AnimatePresence mode="wait">
        <motion.div
          key={currentMetricIndex}
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: -20 }}
          transition={{ duration: 0.3 }}
          className={`w-full max-w-sm bg-gradient-to-br ${currentMetric.color} border-2 ${currentMetric.border} rounded-xl p-6 shadow-lg`}
        >
          <div className="flex flex-col items-center gap-3">
            <span className="text-5xl">{currentMetric.icon}</span>
            <div className="text-center">
              <div className={`font-bold ${currentMetric.text} text-lg flex items-center justify-center gap-2`}>
                {currentMetric.name}
                {currentMetric.subtitle && (
                  <span className="text-xs font-normal opacity-70">({currentMetric.subtitle})</span>
                )}
              </div>
              <div className={`${currentMetric.text} text-3xl font-bold mt-2`}>
                {currentMetric.value}
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.5 }}
        transition={{ delay: 0.5 }}
        className="text-xs text-muted-foreground text-center"
      >{t('ui.text.create_custom_metrics_for_anything_you_want_to_tra')}</motion.p>
    </div>
  );
};


// Elegant horizontal wave of contribution-colored squares
const LoadingWave = () => {
  const squareCount = 9;
  const intensities = useMemo(
    () => Array.from({ length: squareCount }, (_, i) => {
      // Create a gradient pattern across the row
      const center = Math.floor(squareCount / 2);
      const dist = Math.abs(i - center);
      return Math.max(1, 4 - dist);
    }),
    []
  );

  return (
    <div className="flex items-center gap-[3px]">
      {intensities.map((intensity, i) => (
        <motion.div
          key={i}
          className="w-[6px] h-[6px] rounded-[1.5px]"
          style={{
            backgroundColor: `hsl(var(--contrib-${intensity}))`,
          }}
          animate={{
            opacity: [0.15, 0.9, 0.15],
            scale: [0.8, 1.15, 0.8],
          }}
          transition={{
            duration: 1.8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: i * 0.12,
          }}
        />
      ))}
    </div>
  );
};

export default Loading;
